/**
 * RouteHistorySubmenu Component
 *
 * Displays route history for a selected campaign in a submenu format.
 * Shows the last 5 routes with date, stops, and distance.
 * Includes green checkbox indicator for active route.
 */

import React from 'react';
import { Check, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import type { RouteHistory } from '@shared/types/api';

export interface RouteHistorySubmenuProps {
  /**
   * List of route history items for the campaign
   */
  routeHistory: RouteHistory[];

  /**
   * Currently active route ID (persisted)
   */
  activeRouteId: string | null;

  /**
   * Whether route history is loading
   */
  isLoading: boolean;

  /**
   * Called when user selects a route as active
   */
  onSelectRoute: (routeId: string) => void;

  /**
   * Called when user clicks "View All"
   */
  onViewAll?: () => void;

  /**
   * Maximum routes to display (default: 8)
   */
  limit?: number;
}

/**
 * Format date to "Dec 6, 2025" format
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format route metadata string (created date, stops, distance)
 */
const formatRouteMetadata = (route: RouteHistory): string => {
  const date = formatDate(route.created_at);
  const stops = route.total_stops;
  const miles = Math.round(route.total_distance_miles);
  return `${date} \u2022 ${stops} stops \u2022 ${miles} mi`;
};

/**
 * Loading skeleton for route item (two-line format)
 */
const RouteItemSkeleton: React.FC = () => (
  <div className="flex items-start gap-2 px-3 py-2 animate-pulse">
    <div className="w-4 h-4 bg-dark-border rounded mt-0.5" />
    <div className="flex-1 space-y-1.5">
      <div className="h-4 bg-dark-border rounded w-3/4" />
      <div className="h-3 bg-dark-border/60 rounded w-1/2 ml-1" />
    </div>
  </div>
);

/**
 * RouteHistorySubmenu - Displays route history with active route selection
 */
export const RouteHistorySubmenu: React.FC<RouteHistorySubmenuProps> = ({
  routeHistory,
  activeRouteId,
  isLoading,
  onSelectRoute,
  onViewAll,
  limit = 8,
}) => {
  const displayedRoutes = routeHistory.slice(0, limit);
  const hasMore = routeHistory.length > limit;

  // Empty state
  if (!isLoading && routeHistory.length === 0) {
    return (
      <div className="border-t border-dark-border/30 mt-1 pt-2 px-3 pb-2">
        <div className="flex items-center gap-2 text-xs text-dark-muted">
          <MapPin className="w-3 h-3" />
          <span>No routes calculated yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-dark-border/30 mt-1 pt-1">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1">
        <span className="text-xs font-medium text-dark-muted uppercase tracking-wide">
          Route History
        </span>
        {routeHistory.length > 0 && (
          <span className="text-xs text-dark-muted">
            ({routeHistory.length} route{routeHistory.length !== 1 ? 's' : ''})
          </span>
        )}
      </div>

      {/* Route List - Scrollable with visible scrollbar (shows 5 routes, scroll for more) */}
      <div className="max-h-[260px] overflow-y-auto scrollbar-dark">
        {isLoading ? (
          // Loading skeletons
          <>
            <RouteItemSkeleton />
            <RouteItemSkeleton />
            <RouteItemSkeleton />
          </>
        ) : (
          // Route items - Two-line format
          displayedRoutes.map((route) => {
            const isActive = route.route_id === activeRouteId;
            const routeName = route.route_name || 'Unnamed Route';
            return (
              <button
                key={route.route_id}
                onClick={() => onSelectRoute(route.route_id)}
                className={`
                  w-full flex items-start gap-2 px-3 py-2
                  text-left transition-all duration-150
                  ${isActive
                    ? 'bg-green-500/10'
                    : 'hover:bg-dark-surface/50'
                  }
                `}
              >
                {/* Active indicator */}
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  {isActive ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border border-dark-border/30" />
                  )}
                </div>

                {/* Route info - Two lines */}
                <div className="flex-1 min-w-0">
                  {/* Route Name - Primary line */}
                  <div className={`text-sm font-medium truncate ${isActive ? 'text-green-400' : 'text-dark-text'}`}>
                    {routeName}
                  </div>
                  {/* Metadata - Secondary line (indented, italic) */}
                  <div className="text-xs text-dark-muted italic pl-1 truncate">
                    {formatRouteMetadata(route)}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* View All link */}
      {hasMore && onViewAll && !isLoading && (
        <button
          onClick={onViewAll}
          className="
            w-full flex items-center justify-center gap-1
            px-3 py-2 mt-1
            text-xs text-brand-500 hover:text-brand-400
            border-t border-dark-border/30
            transition-all duration-150
          "
        >
          View All Routes
          <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default RouteHistorySubmenu;
